import { isNativeApp, startNativeWatcher, stopNativeWatcher } from './native';

/*
 * locationService — ONE GPS watcher shared by every feature that needs the
 * device's position (always-on presence pings + in-progress trip breadcrumbs).
 *
 * Running two native watchers would spawn two foreground services and two
 * ongoing notifications and drain the battery twice. Instead, consumers
 * subscribe here; the watcher starts on the first subscriber and stops on the
 * last. The watcher runs at the FINEST distanceFilter any subscriber asks for,
 * so a trip (fine) and presence (coarse) coexist without a second service.
 */

const NATIVE = isNativeApp();
const WEB_RETRY_MS = 15_000;

const subscribers = new Map(); // token -> { onPoint, onError, distanceFilter }
let nativeWatchId = null;      // string id | 'starting' | null
let webWatchId = null;
let retryTimer = null;
let currentFilter = null;      // distanceFilter the native watcher was started with
let tokenSeq = 0;

const finestFilter = () => {
  let min = Infinity;
  for (const s of subscribers.values()) min = Math.min(min, s.distanceFilter ?? 25);
  return Number.isFinite(min) ? min : 25;
};

const emitPoint = (p) => { for (const s of subscribers.values()) { try { s.onPoint?.(p); } catch { /* one bad consumer must not stop the rest */ } } };
const emitError = (e) => { for (const s of subscribers.values()) { try { s.onError?.(e); } catch { /* noop */ } } };

function normalize(loc, web) {
  if (web) {
    const c = loc.coords;
    return {
      lat: c.latitude, lng: c.longitude,
      accuracy: Number.isFinite(c.accuracy) ? c.accuracy : undefined,
      speed: Number.isFinite(c.speed) && c.speed >= 0 ? c.speed : undefined,
      heading: Number.isFinite(c.heading) ? c.heading : undefined,
      altitude: Number.isFinite(c.altitude) ? c.altitude : undefined,
      timestamp: new Date(loc.timestamp || Date.now()).toISOString(),
    };
  }
  return loc; // native.startNativeWatcher already normalizes
}

async function startNative() {
  if (nativeWatchId != null) return;
  nativeWatchId = 'starting';
  currentFilter = finestFilter();
  try {
    const id = await startNativeWatcher({
      distanceFilter: currentFilter,
      onPoint: (p) => emitPoint(p),
      onError: (e) => emitError(e),
    });
    if (nativeWatchId === 'starting') nativeWatchId = id;
    else stopNativeWatcher(id); // stopped while starting
  } catch (e) {
    nativeWatchId = null;
    emitError(e);
  }
}

function startWeb() {
  if (webWatchId != null || !('geolocation' in navigator)) {
    if (!('geolocation' in navigator)) emitError({ message: 'Geolocation not supported' });
    return;
  }
  webWatchId = navigator.geolocation.watchPosition(
    (pos) => emitPoint(normalize(pos, true)),
    (err) => {
      emitError(err);
      if (webWatchId != null) navigator.geolocation.clearWatch(webWatchId);
      webWatchId = null;
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => { retryTimer = null; if (subscribers.size) startWeb(); }, WEB_RETRY_MS);
    },
    { enableHighAccuracy: true, maximumAge: 3000, timeout: 20000 },
  );
}

function start() { if (NATIVE) startNative(); else startWeb(); }

function stop() {
  if (nativeWatchId != null) {
    if (nativeWatchId !== 'starting') stopNativeWatcher(nativeWatchId);
    nativeWatchId = null;
  }
  if (webWatchId != null && navigator.geolocation) { navigator.geolocation.clearWatch(webWatchId); webWatchId = null; }
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  currentFilter = null;
}

// Restart the native watcher only if a new subscriber needs a finer filter.
async function maybeRetune() {
  if (!NATIVE || nativeWatchId == null || nativeWatchId === 'starting') return;
  const want = finestFilter();
  if (currentFilter != null && want < currentFilter) {
    stop();
    start();
  }
}

export function subscribeLocation({ onPoint, onError, distanceFilter = 25 }) {
  const token = ++tokenSeq;
  subscribers.set(token, { onPoint, onError, distanceFilter });
  if (subscribers.size === 1) start();
  else maybeRetune();
  return () => {
    subscribers.delete(token);
    if (subscribers.size === 0) stop();
  };
}

export const isNativeLocation = () => NATIVE;
